import { useTranslation } from '@omni/kit/hooks/useTranslation';
import React, { ReactElement, useLayoutEffect, useRef, useState } from 'react';
import {
  Animated,
  Platform,
  StyleSheet,
  TextProps,
  TextStyle,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import BorderRadius from '../theming/BorderRadius';
import Colors from '../theming/Colors';
import { ThemeContextType, useThemeContext } from '../theming/ThemeContext';
import { hexToRGBA } from '../utilities/colorTools';
import KitTouchable from './KitTouchable';

const serifIosFont = 'NewYorkMedium-Regular';
const serifRegularAndroidFont = 'NotoSerif-Regular';
const serifBoldAndroidFont = 'NotoSerif-Bold';
const semiBoldAndroidFont = 'Roboto-Medium';
const boldAndroidFont = 'Roboto-Bold';
const extraBoldAndroidFont = 'Roboto-Black';
const webFont = 'inherit'; // In case of web inherit font-family from root element (html, body)

//******************************************************************************
// Types
//******************************************************************************
export interface IKitTextProps extends TextProps {
  testID?: string;

  // Properties
  bold?: boolean; // 700
  center?: boolean;
  expandable?: boolean;
  extraBold?: boolean; // 800
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: number;
  semiBold?: boolean; // 600
  serif?: boolean;
  lineHeight?: number;
  fadeColor?: string;

  // Colors
  black?: boolean;
  brandColor?: boolean;
  color?: string;
  gray?: boolean;
  white?: boolean;

  // Styles
  body1?: boolean;
  h1?: boolean;
  h2?: boolean;
  subtitle?: boolean;

  // Margin
  margin?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  marginHorizontal?: number;
  marginVertical?: number;

  colorForScheme?: any;
}

const androidReadyFont = (
  { fontFamily, h1, h2, semiBold, extraBold, bold }: IKitTextProps,
  theme: ThemeContextType
): string | undefined => {
  if (Platform.OS === 'android') {
    const { androidWeights: weights } = theme;

    if (weights) {
      if (weights?.extraBold && (h1 || extraBold)) return weights.extraBold;
      else if (weights?.bold && (h2 || bold)) return weights.bold;
      else if (weights?.semiBold && semiBold) return weights.semiBold;
      else if (weights?.regular) return weights?.regular;
    }

    if (h1 || extraBold) return extraBoldAndroidFont;
    else if (h2 || semiBold) return semiBoldAndroidFont;
    else if (bold) return boldAndroidFont;
  } else if (Platform.OS === 'web') {
    return fontFamily || webFont;
  } else {
    return fontFamily || theme.fontFamily;
  }
};

// Helper function to extract margin style from margin props
const getMarginStyle = (props: IKitTextProps) => {
  const style: any = {};

  if (props.margin !== undefined) style.margin = props.margin;

  if (props.marginTop !== undefined) style.marginTop = props.marginTop;

  if (props.marginBottom !== undefined) style.marginBottom = props.marginBottom;

  if (props.marginLeft !== undefined) style.marginLeft = props.marginLeft;

  if (props.marginRight !== undefined) style.marginRight = props.marginRight;

  if (props.marginHorizontal !== undefined) {
    style.marginHorizontal = props.marginHorizontal;
  }

  if (props.marginVertical !== undefined) {
    style.marginVertical = props.marginVertical;
  }

  return style;
};

// Helper function to compute dynamic text styles
const getTextStyle = (
  props: IKitTextProps,
  theme: ThemeContextType
): TextStyle => {
  const { colorForScheme, fontFamily: themeFontFamily } = theme;

  const {
    fontSize,
    h1,
    h2,
    body1,
    subtitle,
    lineHeight,
    center,
    fontWeight,
    extraBold,
    bold,
    semiBold,
    color,
    black,
    white,
    brandColor,
    gray,
    serif,
    fontFamily = themeFontFamily,
  } = props;

  // Compute font size
  let computedFontSize = 16;

  if (fontSize) computedFontSize = fontSize;
  else if (h1) computedFontSize = 22;
  else if (h2) computedFontSize = 20;
  else if (subtitle) computedFontSize = 18;
  else if (body1) computedFontSize = 16;

  // Compute line height
  let computedLineHeight = 22;

  if (lineHeight) computedLineHeight = lineHeight;
  else if (h1 || fontSize === 22) computedLineHeight = 30;
  else if (h2 || fontSize === 20) computedLineHeight = 24;
  else if (subtitle || fontSize === 18) computedLineHeight = 22;
  else if (body1 || fontSize === 16) computedLineHeight = 22;
  else if (fontSize && fontSize >= 24) {
    computedLineHeight = Math.floor(fontSize * 1.2);
  }

  // Compute font family
  let computedFontFamily: string | undefined;

  if (serif) {
    if (Platform.OS === 'web') computedFontFamily = webFont;
    else if (Platform.OS === 'ios') computedFontFamily = serifIosFont;
    else if (bold) computedFontFamily = serifBoldAndroidFont;
    else computedFontFamily = serifRegularAndroidFont;
  } else if (fontFamily) {
    computedFontFamily = fontFamily;
  } else {
    computedFontFamily = androidReadyFont(props, theme);
  }

  // Compute font weight (iOS/Web only)
  let computedFontWeight: TextStyle['fontWeight'];

  if (Platform.OS !== 'android') {
    if (fontWeight) computedFontWeight = fontWeight as TextStyle['fontWeight'];
    else if (h1 || extraBold) computedFontWeight = '800';
    else if (h2 || bold) computedFontWeight = '700';
    else if (semiBold) computedFontWeight = '600';
    else computedFontWeight = '400';
  }

  // Compute color
  let computedColor: string = Colors.N500;

  if (color) computedColor = color;
  else if (black) computedColor = Colors.N900;
  else if (white) computedColor = Colors.N0;
  else if (brandColor) computedColor = Colors.brand;
  else if (gray) computedColor = Colors.N500;
  else if (h1 || h2) {
    computedColor =
      colorForScheme?.({
        light: Colors.N900,
        dark: Colors.N0,
      }) || Colors.N900;
  } else if (subtitle) {
    computedColor =
      colorForScheme?.({ light: Colors.N900, dark: Colors.N100 }) ||
      Colors.N900;
  } else {
    computedColor =
      colorForScheme?.({ light: Colors.N500, dark: Colors.N400 }) ||
      Colors.N500;
  }

  // Combine all styles
  const style: TextStyle = {
    fontSize: computedFontSize,
    lineHeight: computedLineHeight,
    color: computedColor,
    ...(center && { textAlign: 'center' }),
    ...(computedFontFamily && { fontFamily: computedFontFamily }),
    ...(computedFontWeight && { fontWeight: computedFontWeight }),
    ...getMarginStyle(props),
  };

  return style;
};

const KitText = (props: IKitTextProps): ReactElement => {
  const theme = useThemeContext();
  const { fontFamily: themeFontFamily } = theme;
  const [isClipped, setIsClipped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef<HTMLElement | null>(null);
  const { fontFamily = themeFontFamily, style = {} } = props;

  useLayoutEffect(() => {
    // Web only
    if (Platform.OS === 'web' && props.expandable && ref.current) {
      setIsClipped(
        ref.current.getBoundingClientRect().height < ref.current.scrollHeight
      );
    }
  }, [props.expandable]);

  const { style: _style, ...restProps } = props;

  // Compute dynamic styles
  const computedStyle = getTextStyle(props, theme);
  const finalStyle = [computedStyle, style];

  const textElem = (
    <Animated.Text
      {...restProps}
      // @ts-ignore
      ref={ref}
      allowFontScaling={false}
      style={finalStyle}
      numberOfLines={isExpanded ? 0 : props.numberOfLines}
      // Native only
      onTextLayout={
        props.expandable
          ? (e) =>
              setIsClipped(
                props.numberOfLines !== undefined &&
                  e.nativeEvent.lines.length >= props.numberOfLines
              )
          : undefined
      }
    >
      {props.children}
    </Animated.Text>
  );

  if (props.expandable) {
    return (
      <View style={{ position: 'relative' }}>
        {textElem}
        <TruncateButton
          isExpanded={isExpanded}
          show={isClipped}
          onPress={() => setIsExpanded(!isExpanded)}
          fontFamily={fontFamily}
          fadeColor={props.fadeColor}
        />
      </View>
    );
  } else {
    return textElem;
  }
};

export default KitText;

/**
 * Button can appear either at the end of a line or at the following line
 */
export const TruncateButton = ({
  isExpanded,
  onPress,
  show,
  fontFamily,
  fadeColor,
  fontSize,
  lineHeight,
}: {
  isExpanded: boolean;
  onPress: () => void;
  show: boolean;
  fontFamily?: string;
  fadeColor?: string;
  fontSize?: number;
  lineHeight?: number;
}): ReactElement | null => {
  const { colorForScheme } = useThemeContext();
  const { t } = useTranslation('common');
  const [r, g, b] = hexToRGBA(
    fadeColor ||
      colorForScheme?.({
        light: Colors.N0,
        dark: Colors.N1000,
      }) ||
      Colors.N0
  );

  if (!show) return null;

  const customFontFamily =
    Platform.OS === 'android' ? boldAndroidFont : fontFamily;

  return (
    <LinearGradient
      start={{ x: 0, y: 0 }}
      end={{ x: 0.4, y: 0 }}
      locations={[0, Platform.OS === 'web' ? 0.35 : 1]}
      // @ts-ignore
      colors={[`rgba(${r}, ${g}, ${b}, 0)`, `rgba(${r}, ${g}, ${b}, 1)`]}
      style={
        !isExpanded
          ? {
              position: 'absolute',
              right: 0,
              bottom: 0,
              paddingLeft: 40,
            }
          : undefined
      }
    >
      <KitTouchable
        onPress={onPress}
        ripple={false}
        underlayColor={
          Platform.OS === 'android'
            ? colorForScheme?.({
                light: Colors.N100,
                dark: Colors.N900,
              })
            : ''
        }
        style={{
          borderRadius: BorderRadius.l,
        }}
      >
        <Animated.Text
          allowFontScaling={false}
          style={{
            fontFamily: customFontFamily,
            fontSize: fontSize || 16,
            lineHeight: lineHeight || 22,
            fontWeight: Platform.OS === 'android' ? undefined : '700',
            color:
              colorForScheme?.({
                light: Colors.N900,
                dark: Colors.N0,
              }) ?? Colors.N900,
            textTransform: 'capitalize',
          }}
        >
          {isExpanded ? t('less') : t('more')}
        </Animated.Text>
      </KitTouchable>
    </LinearGradient>
  );
};
